/**
 * Parent Welcome & Resource Hub — public page linked from the parent welcome email.
 * Houses: key dates, family orientation scheduler, academic calendar, supply list,
 * account-setup instructions, "what to expect", and Command Center quick links.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays, GraduationCap, ClipboardList, Users, ShieldCheck, Loader2, CheckCircle2,
  LayoutDashboard, MessageSquare, ShoppingBag, BookOpen, Mail, KeyRound, Laptop, ChevronRight
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const GOLD = '#C9A961';
const ORANGE = '#F97316';

const KEY_DATES = [
  ['Aug 18, 2026', 'Athletic & Academic Open House — Student Combine'],
  ['Aug 24–27, 2026', 'Family Orientation Week (virtual & in-person)'],
  ['Sept 8, 2026', 'First Day of Classes — Semester 1 begins'],
  ['Sept 24, 2026', 'Back-to-School Evening · 5:30–7:00 PM'],
  ['Oct 30, 2026', 'Fall Festival & Community Celebration'],
  ['Nov 25–27, 2026', 'Thanksgiving Recess'],
  ['Dec 21 – Jan 1', 'Winter Break'],
  ['Jan 11, 2027', 'Classes Resume'],
  ['Feb 12, 2027', 'Parent–Teacher Conferences'],
  ['Mar 22–26, 2027', 'Spring Break'],
  ['May 3–7, 2027', 'Teacher Appreciation Week'],
  ['June 25, 2027', 'Last Day of School & Closing Celebration'],
];

const SUPPLIES = [
  { h: 'Digital & Movement Gear (Optional — provided on campus)', items: [
    '1:1 Digital Device (iPad, laptop, or tablet) — a device is available at school if your scholar doesn’t bring one',
    'Wired headphones / earbuds (3.5mm or USB-C) — also available on hand',
    'Phone holder with desktop tripod — available in project spaces',
    '1–2 pairs barefoot grip socks or low-profile training shoes',
  ]},
  { h: 'Color-Coded Notebooks & Program Binders (Recommended)', items: [
    'Blue notebook — English Language Arts', 'Green notebook — Science & STEM',
    'Yellow notebook — History & Social Studies', 'Red grid/graph notebook — Mathematics',
    '1.5" portfolio binder + 100 clear page protectors', '2" heavy-duty binder + 5-tab dividers (Elite Electives)',
    '1 snap/velcro pocket folder (Entrepreneurship)',
  ]},
  { h: 'Daily Pencil Pouch & Math Toolkit (Recommended)', items: [
    'Heavy-duty zippered pencil pouch', '#2 or mechanical pencils (extra lead & erasers)',
    'Black & blue ballpoint pens (3 each)', 'Fine-tip dry erase markers (4-pack) + eraser',
    'Index card case + 100 index cards (3"×5")', '12" standard/metric ruler',
    'Clear protractor & metal bow compass',
    'Scientific calculator — Gr 5–6: TI-30Xa · Gr 7–8: TI-30XIIS (or equivalent)',
  ]},
  { h: 'Cohort Shared Supplies (Suggested Donations)', items: [
    '3 boxes facial tissues', '2 canisters disinfecting wipes', '1 pump hand sanitizer', '1 roll painter’s/masking tape',
  ]},
];

const authHeaders = () => ({});

const ParentWelcome = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState([]);
  const [form, setForm] = useState({ family_name: '', parent_email: '', student_name: '', phone: '', date: '', start: '' });
  const [booking, setBooking] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/academy/orientation/slots').then((r) => setDays(r.data.days)).catch(() => {});
  useEffect(() => { load(); }, []);

  const pick = (date, start) => setForm((f) => ({ ...f, date, start }));

  const submit = async () => {
    const { family_name, parent_email, student_name, date, start } = form;
    if (!family_name || !parent_email || !student_name) { toast.error('Please add your name, email, and your student’s name.'); return; }
    if (!date || !start) { toast.error('Please select an orientation time slot.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/academy/orientation/book', form);
      setBooking(r.data.booking);
      toast.success('Orientation booked! A confirmation will follow.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not book that slot'); load(); }
    finally { setBusy(false); }
  };

  const links = [
    { icon: LayoutDashboard, t: 'Parent Command Center', d: 'Attendance, mastery & grades', go: () => navigate('/academy/login') },
    { icon: CalendarDays, t: 'Academic Calendar', d: 'Full year at a glance', go: () => document.getElementById('calendar')?.scrollIntoView({ behavior: 'smooth' }) },
    { icon: ClipboardList, t: 'Supply List', d: 'Recommendations (device provided)', go: () => document.getElementById('supplies')?.scrollIntoView({ behavior: 'smooth' }) },
    { icon: BookOpen, t: 'Family Handbook', d: 'Coming soon — reviewed at orientation', go: () => toast.info('The Family Handbook will be shared at your orientation.') },
    { icon: MessageSquare, t: 'Communication Hub', d: 'Messages & announcements', go: () => navigate('/academy/login') },
    { icon: ShoppingBag, t: 'Order Uniforms', d: 'PRSVR team store', go: () => navigate('/') },
  ];

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#050508' }} data-testid="parent-welcome">
      {/* Hero */}
      <div className="relative border-b-2 border-zinc-900 px-6 py-16" style={{ background: 'linear-gradient(to bottom, #0A0A0E, #050508)' }}>
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: `linear-gradient(${ORANGE} 1px, transparent 1px), linear-gradient(90deg, ${ORANGE} 1px, transparent 1px)`, backgroundSize: '56px 56px' }} />
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="text-[10px] font-black uppercase tracking-[0.35em]" style={{ color: GOLD }}>Scholar Athlete Academy · PRSVR School</div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight mt-3">Welcome to the 2026–27 Season</h1>
          <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto mt-4 leading-relaxed">
            We’re thrilled your family has joined the Academy. This page is your home base — schedule your family orientation,
            review the calendar and supply list, and set up your accounts. — <span className="text-white font-bold">Margaret, Scholar Athlete Academy</span>
          </p>
          <div className="grid sm:grid-cols-3 gap-px bg-white/[0.06] mt-8 border border-white/[0.06]">
            {[['First Day of Classes', 'September 8, 2026'], ['Orientation Week', 'August 24–27, 2026'], ['Last Day of School', 'June 25, 2027']].map(([k, v]) => (
              <div key={k} className="p-5" style={{ backgroundColor: '#0A0A0E' }}>
                <div className="text-[10px] uppercase tracking-widest text-gray-500">{k}</div>
                <div className="text-lg font-black mt-1" style={{ color: ORANGE }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-14 space-y-16">
        {/* Family Hub magic-link CTA */}
        <section data-testid="family-hub-cta">
          <div className="border-2 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" style={{ borderColor: '#2447D855', background: '#2447D80a' }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#2447D822' }}>
              <LayoutDashboard className="w-6 h-6" style={{ color: '#6b8afd' }} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-black text-white">Track Your Scholar — Live Family Hub</h3>
              <p className="text-sm text-zinc-400 mt-1">See your scholar's Scholar Card, daily quests, mastery, and coach materials. Get a private, passwordless link sent to your inbox.</p>
            </div>
            <a href="/academy/family" className="inline-flex items-center gap-2 px-5 py-3 font-black text-xs uppercase tracking-widest text-white rounded-lg whitespace-nowrap" style={{ backgroundColor: '#2447D8' }} data-testid="family-hub-cta-btn">
              Open Family Hub <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </section>

        {/* Orientation Scheduler */}
        <section data-testid="orientation-scheduler">
          <SectionHead icon={CalendarDays} kicker="Action Required" title="Schedule Your Family Orientation" sub="Book one 2-hour window the week of Aug 24–27. We’ll cover lunch, absences, communication, and your scholar’s academic & leadership goals." />
          {booking ? (
            <div className="mt-6 border-2 border-emerald-500/50 bg-emerald-500/10 p-6 text-center" data-testid="orientation-confirmed">
              <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto" />
              <div className="text-lg font-black mt-2">You’re booked!</div>
              <div className="text-sm text-zinc-300 mt-1">{booking.day_label} · {booking.slot_label}</div>
              <div className="text-xs text-zinc-500 mt-1">{booking.family_name} · {booking.student_name}</div>
              <button onClick={() => setBooking(null)} className="mt-4 text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-white" data-testid="orientation-reschedule">Change my time</button>
            </div>
          ) : (
            <div className="mt-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={form.family_name} onChange={(e) => setForm((f) => ({ ...f, family_name: e.target.value }))} placeholder="Parent / family name" className="bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none" data-testid="orient-family-name" />
                <input value={form.student_name} onChange={(e) => setForm((f) => ({ ...f, student_name: e.target.value }))} placeholder="Student name" className="bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none" data-testid="orient-student-name" />
                <input value={form.parent_email} onChange={(e) => setForm((f) => ({ ...f, parent_email: e.target.value }))} placeholder="Parent email" className="bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none" data-testid="orient-email" />
                <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Phone (optional)" className="bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none" data-testid="orient-phone" />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {days.map((d) => (
                  <div key={d.date} className="border border-zinc-800">
                    <div className="px-3 py-2 text-xs font-black uppercase tracking-wider bg-white/[0.03]">{d.label}</div>
                    <div className="p-2 space-y-1.5">
                      {d.slots.map((s) => {
                        const sel = form.date === d.date && form.start === s.start;
                        return (
                          <button key={s.start} disabled={!s.available} onClick={() => pick(d.date, s.start)}
                            className={`w-full text-left px-2.5 py-2 text-[11px] font-bold border transition-colors ${sel ? 'bg-orange-500 text-black border-orange-500' : s.available ? 'border-zinc-700 text-zinc-300 hover:border-white' : 'border-zinc-900 text-zinc-700 line-through cursor-not-allowed'}`}
                            data-testid={`orient-slot-${d.date}-${s.start}`}>
                            {s.label}{!s.available && ' · booked'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={submit} disabled={busy} className="flex items-center gap-2 px-6 py-3 font-black text-xs uppercase tracking-widest text-black" style={{ backgroundColor: ORANGE }} data-testid="orient-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Confirm Orientation <ChevronRight className="w-4 h-4" /></>}
              </button>
            </div>
          )}
        </section>

        {/* Account setup */}
        <section data-testid="account-setup">
          <SectionHead icon={KeyRound} kicker="Set Up First" title="Create Your Accounts" sub="Please write down every username and password as you create them — you’ll need them all year." />
          <div className="mt-6 space-y-3">
            <Step n="1" title="Create your Parent account on the SAA platform" body="This is your Parent Command Center — attendance, mastery, grades, messages, and uniform orders." action={{ label: 'Create Parent Account', go: () => navigate('/academy/enroll') }} />
            <Step n="2" title="Create your Student’s SAA account" body="Your scholar gets their own login for daily Game Plans, worksheets, and their Digital Portfolio." action={{ label: 'Create Student Account', go: () => navigate('/academy/enroll') }} />
            <Step n="3" title="Create a NEW student Gmail: FirstName + Last Initial + saa" body="Example: BrandonWsaa@gmail.com. This is where we send all school communication and how your scholar accesses Google Classroom when needed." icon={Mail} />
            <div className="flex items-start gap-3 p-4 border-2 border-amber-500/40 bg-amber-500/10">
              <ShieldCheck className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <p className="text-xs text-zinc-300">Keep a single note (or password manager) with all three logins — parent, student SAA, and the new student Gmail. We’ll verify them together at orientation.</p>
            </div>
          </div>
        </section>

        {/* Supply list */}
        <section id="supplies" data-testid="supply-list">
          <SectionHead icon={ClipboardList} kicker="Recommendations" title="Supply List" sub="Everything here is a recommendation. A 1:1 device and classroom tech are available at school if your scholar doesn’t bring their own." />
          <a href="/downloads/prsvr-supply-list.png" download="PRSVR-Supply-List.png" className="mt-4 inline-flex items-center gap-2 px-5 py-3 font-black text-xs uppercase tracking-widest text-black" style={{ backgroundColor: ORANGE }} data-testid="supply-download-btn">
            <ClipboardList className="w-4 h-4" /> Download Supply List (Image)
          </a>
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            {SUPPLIES.map((cat, i) => (
              <div key={i} className="border border-zinc-800 p-5" style={{ backgroundColor: '#0A0A0E' }}>
                <div className="text-xs font-black uppercase tracking-wider mb-3" style={{ color: GOLD }}>{cat.h}</div>
                <ul className="space-y-1.5">{cat.items.map((it, j) => (<li key={j} className="flex gap-2 text-[13px] text-zinc-300 leading-snug"><span style={{ color: ORANGE }}>•</span>{it}</li>))}</ul>
              </div>
            ))}
          </div>
        </section>

        {/* Calendar */}
        <section id="calendar" data-testid="academic-calendar">
          <SectionHead icon={CalendarDays} kicker="2026–27" title="Academic Calendar" sub="Academic block runs Mon–Thu, 12:00–3:00 PM; Fridays are Master Sprints (labs, review, 1-on-1 tutoring)." />
          <div className="mt-6 border border-zinc-800">
            {KEY_DATES.map(([d, e], i) => (
              <div key={i} className="grid grid-cols-[130px_1fr] sm:grid-cols-[180px_1fr] gap-3 p-4 border-b border-zinc-900 last:border-0">
                <div className="text-xs font-mono font-bold" style={{ color: ORANGE }}>{d}</div>
                <div className="text-sm text-zinc-300">{e}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <a href="/downloads/academic-calendar-2026-27.pdf"
               download="SAA-Academic-Calendar-2026-27.pdf"
               className="inline-flex items-center gap-2 px-5 py-3 font-black text-xs uppercase tracking-widest text-black"
               style={{ backgroundColor: ORANGE }}
               data-testid="calendar-download-pdf-btn">
              <CalendarDays className="w-4 h-4" /> Download Calendar (PDF)
            </a>
            <a href="/downloads/academic-calendar-2026-27.ics"
               download="SAA-Academic-Calendar-2026-27.ics"
               className="inline-flex items-center gap-2 px-5 py-3 font-black text-xs uppercase tracking-widest text-white border-2"
               style={{ borderColor: ORANGE, color: '#fff' }}
               data-testid="calendar-download-ics-btn">
              <CalendarDays className="w-4 h-4" /> Add To My Calendar (.ics)
            </a>
          </div>
          <p className="mt-2 text-[11px] text-zinc-500">
            PDF prints on a single page. The .ics file imports every date into Google, Apple, or Outlook calendars in one click.
          </p>
        </section>

        {/* What to expect */}
        <section data-testid="what-to-expect">
          <SectionHead icon={Laptop} kicker="Before Day One" title="What to Expect" />
          <ul className="mt-6 space-y-3">
            {[
              'A focused 3-hour academic block, Monday–Thursday (12:00–3:00 PM), taught live through sports analytics.',
              'Fridays are Master Sprints: 1-on-1 AI/teacher tutoring, film study, capstone reviews, and training-facility work.',
              'Daily Game Plans, mastery-based progression, and a growing Scholar-Athlete Digital Portfolio.',
              'Lunch, absence, and communication procedures will be reviewed in detail at your family orientation.',
            ].map((x, i) => (<li key={i} className="flex gap-3 text-sm text-zinc-300"><CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: ORANGE }} />{x}</li>))}
          </ul>
        </section>

        {/* Command center links */}
        <section data-testid="command-links">
          <SectionHead icon={Users} kicker="Your Home Base" title="Parent Command Center" sub="Everything in one place — more tools (field trips & special activities) coming soon." />
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {links.map((l, i) => (
              <button key={i} onClick={l.go} className="text-left border-2 border-zinc-800 hover:border-orange-500 p-5 transition-colors group" data-testid={`command-link-${i}`}>
                <l.icon className="w-6 h-6 mb-3" style={{ color: ORANGE }} />
                <div className="text-sm font-black text-white">{l.t}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{l.d}</div>
              </button>
            ))}
          </div>
        </section>
      </div>

      <div className="border-t-2 border-zinc-900 px-6 py-8 text-center text-[11px] text-zinc-500">
        Questions? Reply to your welcome email or reach Margaret at Scholar Athlete Academy · PRSVR School.
      </div>
    </div>
  );
};

const SectionHead = ({ icon: Icon, kicker, title, sub }) => (
  <div>
    {kicker && <div className="text-[10px] font-black uppercase tracking-[0.3em] mb-2" style={{ color: GOLD }}>{kicker}</div>}
    <div className="flex items-center gap-2"><Icon className="w-5 h-5" style={{ color: ORANGE }} /><h2 className="text-xl md:text-2xl font-black tracking-tight">{title}</h2></div>
    {sub && <p className="text-sm text-zinc-500 mt-2 max-w-2xl">{sub}</p>}
  </div>
);

const Step = ({ n, title, body, action, icon: Icon }) => (
  <div className="flex items-start gap-4 border border-zinc-800 p-4" style={{ backgroundColor: '#0A0A0E' }}>
    <div className="w-8 h-8 flex items-center justify-center bg-orange-500 text-black font-black text-sm flex-shrink-0">{n}</div>
    <div className="flex-1">
      <div className="text-sm font-black text-white flex items-center gap-2">{Icon && <Icon className="w-4 h-4" style={{ color: '#F97316' }} />}{title}</div>
      <p className="text-xs text-zinc-400 mt-1">{body}</p>
      {action && <button onClick={action.go} className="mt-2 text-[10px] font-black uppercase tracking-widest" style={{ color: '#F97316' }} data-testid="account-step-action">{action.label} →</button>}
    </div>
  </div>
);

export default ParentWelcome;
